<template>
    <div class="flex flex-col">
        <div class="flex justify-center items-center my-6" v-if="showTime && formattedTime">
            <p
                data-testid="time-separator"
                class="mx-4 text-sm font-semibold uppercase text-gray-600"
            >
                {{ formattedTime }}
            </p>
        </div>

        <div
            class="flex items-end"
            :class="{
                'justify-end': !message.isIncoming,
                'animate-highlight-fade rounded-lg p-2 -m-2': isHighlighted,
            }"
            :data-communication-id="message.communicationId"
            :data-message-id="message.id"
            @animationend="emit('highlight-animation-end')"
        >
            <sms-avatar
                class="mr-3"
                data-testid="incoming-avatar"
                :username="leadName"
                v-if="message.isIncoming"
            />

            <div class="max-w-2/3">
                <div class="flex flex-col items-start gap-2" v-if="message.attachments.length > 0">
                    <template v-for="attachment in message.attachments">
                        <sms-attachment-image
                            :attachment="attachment"
                            image-class="max-h-48 w-auto h-auto rounded cursor-pointer"
                            data-sms-attachment="true"
                            data-testid="attachment-image"
                            :key="'img-' + attachment.url"
                            @click="openAttachment(attachment)"
                            v-if="isImageAttachment(attachment.mimeType)"
                        />

                        <button
                            class="flex items-center gap-x-2 px-3 py-2 rounded border bg-gray-50 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                            data-testid="attachment-document"
                            :key="'pdf-' + attachment.url"
                            @click="openAttachmentInNewTab(attachment)"
                            v-else
                        >
                            <icon name="regular/common-file-text" class="h-4 w-4 shrink-0" />
                            <span class="truncate max-w-xs">{{ attachment.filename }}</span>
                        </button>
                    </template>
                </div>

                <div class="relative mb-2" v-if="message.text">
                    <svg
                        class="absolute top-full -mt-4 h-5 w-6 pointer-events-none"
                        :class="bubbleTailClasses"
                        data-testid="message-bubble-tail"
                        viewBox="0 0 18 12"
                    >
                        <path d="M2 1C2 7 7 11 17 11C12 10 10 6 10 1H2Z" fill="currentColor" />
                    </svg>

                    <div
                        class="relative px-2.5 py-1 rounded-lg"
                        :class="message.isIncoming ? 'bg-white' : 'bg-blue-500 text-white'"
                    >
                        <span
                            class="whitespace-pre-wrap"
                            data-testid="text-message"
                            v-html="linkedText"
                        />
                    </div>
                </div>

                <sms-appointment-card
                    :appointment="message.appointment"
                    v-if="!message.isIncoming && message.appointment"
                />

                <div
                    class="flex justify-between mt-1 px-1 text-gray-700 text-sm"
                    :class="{ 'justify-end': !message.isIncoming }"
                >
                    <span data-testid="sender-name">
                        {{ senderName }}
                    </span>

                    <span class="ml-2">{{ message.time }}</span>
                </div>

                <sms-message-failure-indicator
                    :error-code="message.errorCode"
                    :is-read-only="isReadOnly"
                    :is-retrying="isRetrying"
                    @retry="emit('retry')"
                    v-if="isFailed"
                />
            </div>

            <sms-avatar
                class="ml-3"
                data-testid="outgoing-avatar"
                :username="message.senderName"
                v-if="!message.isIncoming"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import autolinker from 'autolinker';
    import { computed } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import ActivixDate from '@/value-objects/ActivixDate.js';
    import { isImageAttachment } from '@/entities/types/ConversationType.ts';
    import type { Message, MessageAttachment } from '@/entities/types/ConversationType.ts';
    import { FAILED_STATUSES } from '@/entities/enums/SmsMessageStatus.ts';
    import SmsAvatar from '@/components/sms/SmsAvatar.vue';
    import SmsAppointmentCard from '@/components/sms/SmsAppointmentCard.vue';
    import SmsMessageFailureIndicator from '@/components/sms/SmsMessageFailureIndicator.vue';
    import useDynamicModal from '@/store/hooks/useDynamicModal.ts';
    import SmsAttachmentImage from '@/components/sms/SmsAttachmentImage.vue';
    import SmsImageViewerModal from '@/components/sms/SmsImageViewerModal.vue';
    import { useAttachmentDownload } from '@/hooks/useAttachmentDownload.ts';

    interface Props {
        message: Message;
        showTime: boolean;
        leadName: string;
        isHighlighted?: boolean;
        isReadOnly?: boolean;
        isRetrying?: boolean;
    }

    interface Emits {
        (e: 'highlight-animation-end'): void;
        (e: 'retry'): void;
    }

    const props = withDefaults(defineProps<Props>(), {
        isReadOnly: false,
        isRetrying: false,
    });
    const emit = defineEmits<Emits>();

    const { t } = useI18n();
    const { openAttachmentInNewTab } = useAttachmentDownload();
    const { openModal } = useDynamicModal();

    const linkedText = computed(() => {
        if (!props.message.text) {
            return '';
        }

        return autolinker.link(props.message.text, {
            sanitizeHtml: true,
            newWindow: true,
        });
    });

    const isFailed = computed<boolean>(() => {
        return (
            !props.message.isIncoming &&
            props.message.status !== null &&
            FAILED_STATUSES.has(props.message.status)
        );
    });

    const formattedTime = computed(() => {
        const date = new ActivixDate(props.message.createdAt);

        return date.toHumanTime();
    });

    const senderName = computed(() => {
        if (props.message.isIncoming) {
            return props.leadName || '';
        }

        if (!props.message.senderName) {
            return t('general.unknown');
        }

        const suffix = props.message.senderIsAuthUser ? ` (${t('general.you')})` : '';

        return `${props.message.senderName}${suffix}`;
    });

    const bubbleTailClasses = computed(() => {
        return props.message.isIncoming
            ? '-left-2 -scale-x-100 text-white'
            : '-right-2 text-blue-500';
    });

    const openAttachment = (attachment: MessageAttachment) => {
        openModal({
            component: SmsImageViewerModal,
            props: {
                attachment,
            },
            unique: true,
        });
    };
</script>

<style scoped>
    @keyframes highlight-fade {
        0% {
            background-color: rgb(254 249 195);
        }
        50% {
            background-color: rgb(254 249 195);
        }
        100% {
            background-color: transparent;
        }
    }

    .animate-highlight-fade {
        animation: highlight-fade 2s ease-out forwards;
    }

    /* v-html content doesn't receive scoped styles — :deep() is required */
    :deep(a) {
        text-decoration: underline;
        color: inherit;
    }

    .bg-white :deep(a) {
        color: theme('colors.blue.600');
    }

    .bg-blue-500 :deep(a) {
        color: white;
    }

    /* Own-message bubbles share the same blue as the default text selection,
       which makes selected text invisible. Override selection colors here. */
    .bg-blue-500 ::selection,
    .bg-blue-500 :deep(*)::selection {
        background-color: white;
        color: theme('colors.blue.700');
    }
</style>
